import { useProcessesProcessShowQuery } from "@/services/backend/processes/api/process-api";
import { ReactNode, useEffect, useState } from "react";
import { Slot } from "radix-ui";
import { Button } from "@/shared/components/ui/button";
import { Plus } from "lucide-react";
import t from "@/lang/lang";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import { useToast } from "@/shared/hooks/use-toast";
import { useHtzWorkOrderCreateMutation } from "@/services/backend/htz/work-order/api/work-order-api";
import { v4 } from "uuid";
import { BranchSelectFrom } from "@/services/backend/company/components/branch-select-from";
import { ServiceObjectSelectFrom } from "@/services/backend/customers/components/service-object-select-from";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Field, FieldLabel } from "@/shared/components/ui/field";
import {
  CommonDialogFooter,
  CommonDialogHeader,
} from "@/shared/components/dialog/dialog-common";

interface Props {
  processId: string;
  onCreated: (workOrderId: string) => void;
  trigger?: ReactNode;
}

export function CreateWorkOrderButton({
  processId,
  onCreated,
  trigger,
}: Props) {
  const [open, setOpen] = useState(false);
  const { data: process } = useProcessesProcessShowQuery({ id: processId });
  const [create, { isLoading, error, reset }] = useHtzWorkOrderCreateMutation();
  const { toast } = useToast();

  const branches = process?.branches ?? [];
  const serviceObjects = process?.serviceObjects ?? [];
  const [branchId, setBranchId] = useState(branches[0] ?? "");
  const [serviceObjectId, setServiceObjectId] = useState(
    serviceObjects[0] ?? "",
  );

  useEffect(() => {
    if (error) {
      toast({ ...parseRTKQueryError(error), variant: "destructive" });
      reset();
    }
  }, [error, reset, toast]);

  const disabled =
    isLoading || !process || branchId === "" || serviceObjectId === "";

  const doCreate = () => {
    if (disabled) {
      return;
    }

    create({
      id: v4(),
      processId: process.id,
      companyId: process.companyId,
      branchId,
      customerId: process.customerId!,
      serviceObjectId,
    })
      .unwrap()
      .then((w) => {
        setOpen(false);
        onCreated(w.id);
        reset();
      });
  };

  if (branches.length === 1 && serviceObjects.length === 1) {
    if (trigger) {
      return <Slot.Root onClick={doCreate}>{trigger}</Slot.Root>;
    }
    return (
      <Button onClick={doCreate} disabled={disabled} size="sm">
        <Plus />
        <span>{t("Arbeitsschein")}</span>
      </Button>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button disabled={disabled} size="sm">
            <Plus />
            <span>{t("Arbeitsschein")}</span>
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <CommonDialogHeader title={t("Arbeitsschein erstellen")} />
        {branches.length !== 1 && (
          <Field>
            <FieldLabel>{t("Niederlassung")}</FieldLabel>
            <BranchSelectFrom
              branchIds={branches}
              value={branchId}
              onValueChange={setBranchId}
            />
          </Field>
        )}
        {serviceObjects.length !== 1 && (
          <Field>
            <FieldLabel>{t("Service Objekt")}</FieldLabel>
            <ServiceObjectSelectFrom
              serviceObjectIds={serviceObjects}
              customerId={process?.customerId}
              value={serviceObjectId}
              onValueChange={setServiceObjectId}
            />
          </Field>
        )}
        <CommonDialogFooter
          isLoading={isLoading}
          disabled={disabled}
          onClickAction={doCreate}
        />
      </DialogContent>
    </Dialog>
  );
}
